/**
 * SecretCodeDialog — feestelijke pop-up na het invoeren van de geheime code.
 * Stijl gespiegeld op de Corvee-dialogs (overlay + kaart, Manrope-titel, Solid/Ghost
 * knoppen). Geen redirect; de gebruiker kan zelf naar het dashboard.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const T = {
  primary: '#526075', primaryDim: '#465469', tertiary: '#635983',
  surface: '#ffffff', text: '#2d3335', secondary: '#556071',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
};

const Icon = ({ name, size = 20, sx = {} }) => (
  <span className="material-symbols-outlined"
    style={{ fontSize: size, fontVariationSettings: "'FILL' 1, 'wght' 500", ...sx }}>{name}</span>
);

const SolidBtn = ({ children, onClick }) => (
  <Box onClick={onClick} sx={{
    display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 2.25, py: 1.1,
    borderRadius: '0.6rem', cursor: 'pointer',
    background: `linear-gradient(135deg, ${T.tertiary}, ${T.primary})`, color: '#fff',
    fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.82rem',
    boxShadow: '0 4px 12px rgba(99,89,131,0.22)', transition: 'all 0.15s',
    '&:hover': { transform: 'translateY(-1px)' },
  }}>{children}</Box>
);

const GhostBtn = ({ children, onClick }) => (
  <Box onClick={onClick} sx={{
    display: 'inline-flex', alignItems: 'center', px: 1.75, py: 1, borderRadius: '0.55rem',
    cursor: 'pointer', border: '1.5px solid rgba(45,51,53,0.12)', bgcolor: 'transparent',
    color: T.secondary, fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.8rem',
    transition: 'all 0.15s', '&:hover': { bgcolor: 'rgba(45,51,53,0.04)' },
  }}>{children}</Box>
);

export default function SecretCodeDialog({ open, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;

  return (
    <Box onClick={onClose} sx={{
      position: 'fixed', inset: 0, zIndex: 2000,
      bgcolor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
    }}>
      <Box onClick={(e) => e.stopPropagation()} sx={{
        bgcolor: T.surface, borderRadius: '0.9rem', width: 420, maxWidth: '100%',
        p: 3, boxShadow: '0 24px 60px rgba(0,0,0,0.3)', textAlign: 'center',
      }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: '50%', bgcolor: T.emeraldBg,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
        }}>
          <Icon name="celebration" size={30} sx={{ color: T.emerald }} />
        </Box>
        <Typography sx={{ fontFamily: T.font, fontSize: '1.15rem', fontWeight: 800, color: T.text, mb: 1 }}>
          Je vond Rick's geheime code! 🎉
        </Typography>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.86rem', color: T.secondary, lineHeight: 1.55, mb: 2.5 }}>
          Je hebt nu <strong>2 minuten</strong> toegang tot geheime widgets in de
          widget-bibliotheek op de hoofdpagina. Zet de editor aan ("Bewerken") en kijk
          onder <em>Geheime widgets</em> — sleep ze naar je dashboard.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <GhostBtn onClick={onClose}>Sluiten</GhostBtn>
          <SolidBtn onClick={() => { onClose(); navigate('/'); }}>
            <Icon name="dashboard" size={16} /> Naar het dashboard
          </SolidBtn>
        </Box>
      </Box>
    </Box>
  );
}
